import React from "react";
import { useObserver } from "mobx-react-lite";
import {
  Grid,
  TextField,
  Slider,
  FormControl,
  FormLabel,
  FormHelperText,
} from "@material-ui/core";
import { Formatter } from "libs";
import { AsyncButton, DatePicker } from "components";
import { useChurnSegmentStore } from "./ChurnSegmentStore";
import { useChurnSegmentFormStore } from "./ChurnSegmentFormStore";

interface Props {
  agencyId: number;
  appId: number;
}

export default ({ agencyId, appId }: Props) => {
  const store = useChurnSegmentStore();
  const formStore = useChurnSegmentFormStore();

  const handleName = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.name = e.target.value;
    },
    [formStore]
  );

  const handlePredictionRate = React.useCallback(
    (_: React.ChangeEvent<{}>, value: number | number[]) => {
      formStore.value.predictionRate =
        typeof value === "number" ? value : value[0];
    },
    [formStore]
  );

  const handleStartAt = React.useCallback(
    (date: Date | null) => {
      if (date) formStore.value.startAt = Formatter.date(date);
    },
    [formStore]
  );

  const handleEndAt = React.useCallback(
    (date: Date | null) => {
      if (date) formStore.value.endAt = Formatter.date(date);
    },
    [formStore]
  );

  const handleTwitterSegmentId = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formStore.value.twitterSegmentId = e.target.value;
    },
    [formStore.value.twitterSegmentId]
  );

  const save = React.useCallback(async () => {
    if (await formStore.save(agencyId, appId)) {
      store.fetchChurnSegment(agencyId, appId);
    }
  }, [agencyId, appId, formStore, store]);

  return useObserver(() => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="名前"
          value={formStore.value.name || ""}
          error={!!formStore.errors.columns.name}
          helperText={formStore.errors.columns.name}
          onChange={handleName}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <FormLabel>予測割合</FormLabel>
          <Slider
            value={formStore.value.predictionRate || 0}
            step={10}
            min={10}
            max={100}
            valueLabelDisplay="auto"
            onChange={handlePredictionRate}
          />
          {formStore.errors.columns.deliveryRate && (
            <FormHelperText error>
              {formStore.errors.columns.deliveryRate}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs="auto">
        <DatePicker
          label="開始日"
          value={formStore.value.startAt || ""}
          error={!!formStore.errors.columns.startAt}
          helperText={formStore.errors.columns.startAt}
          onChange={handleStartAt}
        />
      </Grid>

      <Grid item xs="auto">
        <DatePicker
          label="終了日"
          value={formStore.value.endAt}
          error={!!formStore.errors.columns.endAt}
          helperText={formStore.errors.columns.endAt}
          onChange={handleEndAt}
        />
      </Grid>

      {formStore.value.twitterUserId && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="TwitterオーディエンスID"
            value={formStore.value.twitterSegmentId || ""}
            error={!!formStore.errors.columns.twitterSegmentId}
            helperText={formStore.errors.columns.twitterSegmentId}
            onChange={handleTwitterSegmentId}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <AsyncButton onAwait={save}>保存</AsyncButton>
      </Grid>
    </Grid>
  ));
};
